// pipelineStages.ts — which pipeline stages apply to a given product, and how
// complete it therefore is.
//
// Extracted from AdminPipeline.tsx so it can be tested without a DOM: the brief
// (M5b) requires this be right, because a health metric that is silently wrong
// is worse than one that is missing. Dependency-free on purpose — no React, no
// path aliases — so `npx tsx src/lib/pipelineStages.test.ts` just runs.
//
// ── The correction this module encodes ──────────────────────────────────
// The brief says to compute the stage list "against that `geometry_source`".
// Measured against live data, that is wrong and would introduce the very bug it
// is trying to fix: all three shipped WRT6000 fireplaces are
// `geometry_source = 'parametric_dimensions'` and every one of them ran PBR to
// completion. What actually decides whether PBR runs is `material_source`:
// M4's `swatch_library` resolves finishes from finish_library and calls no
// Stability (Non-negotiable 7), so PBR never runs for a pergola.
//
// Keying on geometry_source would mark those three fireplaces as carrying a
// skipped stage they in fact ran, and would leave a hypothetical
// swatch-finished photogrammetry product scored against a PBR stage it skips.

/** The stages AdminPipeline tracks per product, in pipeline order. */
export const PIPELINE_STAGES = ["pbr", "geometry", "glb_assembly", "asset"] as const;

export type PipelineStage = typeof PIPELINE_STAGES[number];

/** The product fields this module reads. Kept structural so both the page's
 *  row type and test fixtures satisfy it without a shared import. */
export interface StageBearing {
  geometry_source: string | null;
  material_source: string | null;
  pbr_status: string | null;
  geometry_status: string | null;
  glb_assembly_status: string | null;
  asset_status: string | null;
}

const STATUS_FIELD: Record<PipelineStage, keyof StageBearing> = {
  pbr:          "pbr_status",
  geometry:     "geometry_status",
  glb_assembly: "glb_assembly_status",
  asset:        "asset_status",
};

export const STAGE_LABELS: Record<PipelineStage, string> = {
  pbr:          "PBR",
  geometry:     "Geometry",
  glb_assembly: "GLB Assembly",
  asset:        "Asset",
};

/** Material sources that resolve finishes without generating texture maps, so
 *  the PBR stage never runs. M4 added `swatch_library` for pergolas. */
const NON_PBR_MATERIAL_SOURCES = new Set(["swatch_library"]);

/** Whether a stage is part of this product's pipeline at all. */
export function isStageApplicable(p: StageBearing, stage: PipelineStage): boolean {
  if (stage === "pbr") return !NON_PBR_MATERIAL_SOURCES.has(p.material_source ?? "");
  return true;
}

/** The stages this product will actually run, in pipeline order. */
export function applicableStages(p: StageBearing): PipelineStage[] {
  return PIPELINE_STAGES.filter((s) => isStageApplicable(p, s));
}

/**
 * A stage is resolved once it has reached a terminal non-failure state.
 *
 * ── Why this set is wider than "complete" ───────────────────────────────
 * The four status columns do not share one vocabulary, and this predicate is
 * applied to all four. Read off the code that actually writes them:
 *
 *   asset_status         `products_asset_status_check` allows pending |
 *                        processing | generating_texture | complete | failed |
 *                        ready. `process-qa-validation` writes **`ready`** when
 *                        qa_status is `approved` (index.ts:755) and `complete`
 *                        when it is `review` (index.ts:776 — `review` itself is
 *                        an illegal value for this column, so the review path
 *                        borrows `complete` to mean "pipeline finished").
 *   pbr_status           complete | **complete_fallback** | review | skipped |
 *                        processing | failed. `complete_fallback` is written by
 *                        `process-pbr-materials` (index.ts:1403) when the maps
 *                        came from the algorithmic fallback instead of
 *                        Stability. Every other consumer already treats it as
 *                        done — `orchestrate-pipeline:225`,
 *                        `trigger-glb-assembly:144`,
 *                        `process-glb-assembly:2364`.
 *   geometry_status      complete | review | processing | failed | pending.
 *   glb_assembly_status  complete | processing | pending | failed.
 *
 * Testing only `complete` therefore left the **asset stage permanently
 * unresolved for every approved product**, which is the terminal state of the
 * happy path: QA approves, writes `ready`, and the dashboard reports the
 * product as one stage short forever. `needsRetrigger` excludes `asset`, so
 * nothing ever offered a way out of it either. The M5b tests did not catch this
 * because their fixtures assert `asset_status: "complete"` — reachable only via
 * the review path, never via approval.
 *
 * `skipped` is resolved: the pipeline decided not to run it, which is an
 * outcome, not an omission.
 *
 * `review` is deliberately NOT resolved. It is terminal for the *producer* but
 * carries an explicit "a human must look at this" signal, and treating it as
 * done would hide exactly the products that need attention. That is a policy
 * call, not a vocabulary gap — see the note in pipelineStages.test.ts.
 */
function isStageResolved(status: string | null): boolean {
  return (
    status === "complete" ||
    status === "complete_fallback" ||
    status === "ready" ||
    status === "skipped"
  );
}

export interface StageCompletion {
  complete: number;
  /** Denominator is the APPLICABLE stage count, not a global 4. */
  total: number;
  done: boolean;
}

/** How far through its own pipeline this product is. */
export function stageCompletion(p: StageBearing): StageCompletion {
  const stages = applicableStages(p);
  const complete = stages.filter((s) => isStageResolved(p[STATUS_FIELD[s]] as string | null)).length;
  return { complete, total: stages.length, done: complete === stages.length };
}

/**
 * Whether the product has an applicable stage that is missing or failed and
 * could be re-run.
 *
 * Only applicable stages count. A pergola whose `pbr_status` is null has not
 * stalled — nothing was ever going to write it — and offering a retrigger there
 * would invoke Stability on a swatch-finished product, which Non-negotiable 7
 * forbids. `asset` is excluded because it is a downstream rollup rather than an
 * independently triggerable job, matching the previous behaviour.
 */
export function needsRetrigger(p: StageBearing): boolean {
  return applicableStages(p)
    .filter((s) => s !== "asset")
    .some((s) => {
      const status = p[STATUS_FIELD[s]] as string | null;
      return status === null || status === "failed";
    });
}
